import { z } from 'zod';

/** Atlas's id rule for products: lowercase words joined by hyphens (`prod-atlas-flows`). */
export const PRODUCT_ID = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** One of our products, as `GET /catalog/products` lists it. */
export const CatalogProductSchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  description: z.string().default(''),
  url: z.string().nullable().default(null),
  active: z.boolean(),
  features: z.array(z.string()),
});
export type CatalogProduct = z.infer<typeof CatalogProductSchema>;

/**
 * `PUT /catalog/products/{id}` body, with Atlas's rules: 1–50 features,
 * trimmed, non-blank and distinct ignoring case.
 */
export const ProductBodySchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(200),
  description: z.string().trim().max(2000).default(''),
  url: z
    .string()
    .trim()
    .max(500)
    .nullish()
    .transform((v) => v || null),
  active: z.boolean().default(true),
  features: z
    .array(z.string().trim().min(1, 'A feature cannot be blank').max(500))
    .min(1, 'Add at least one feature')
    .max(50, 'At most 50 features')
    .refine((fs) => new Set(fs.map((f) => f.toLowerCase())).size === fs.length, {
      message: 'Features must be distinct',
    }),
});
export type ProductBody = z.infer<typeof ProductBodySchema>;

/** One row of `GET /library/tools`: a tool some finished run proposed. */
export const AtlasToolSchema = z.looseObject({
  tool_key: z.string(),
  name: z.string(),
  source: z.enum(['catalog', 'external']).or(z.string()),
  product_id: z.string().nullable().default(null),
  url: z.string().nullable().default(null),
  first_seen: z.string().nullable().default(null),
  last_seen: z.string().nullable().default(null),
  uses: z.number().default(0),
  runs: z.number().default(0),
  features: z.array(z.string()).default([]),
});
export type AtlasTool = z.infer<typeof AtlasToolSchema>;
